import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import useToken from '../hooks/useToken';
import { SanitizedHTML } from '../utils/Helper';
import authorImg from '../assets/images/avatar.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faCommentAlt } from '@fortawesome/free-regular-svg-icons';
import { API_ENDPOINTS } from '../utils/config';

const Comments = ({ id: postId, updateVariable, targetRef }) => {
    const { token } = useToken();
    const [comments, setComments] = useState([]);
    const [count, setCount] = useState(0);
    const [error, setError] = useState(null);
    const [userReactions, setUserReactions] = useState({});

    const fetchComments = useCallback(async () => {
        if (!postId) return;

        try {
            const { data, status } = await axios.get(`${API_ENDPOINTS.COMMNT_ENDPOINT}/${postId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (status === 401) {
                setError('Unauthorized access.');
                return;
            }

            setComments(data.items || []);
            setCount(data.count || 0);
        } catch (err) {
            console.error('Error fetching comments:', err);
            setError('Failed to load comments.');
        }
    }, [postId, token]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleReplyClick = useCallback((commentId) => {
        updateVariable(commentId);
        targetRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [updateVariable, targetRef]);

    const handleLikeCommentClick = useCallback(async (commentId) => {
        try {
            const res = await axios.post(
                `${API_ENDPOINTS.COMMENT_REACTION_ENDPOINT}/${commentId}`,{ type: 'like' },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setUserReactions((prev) => ({
                ...prev,
                [commentId]: res.data.current_user_reaction,
            }));
    
            fetchComments(); // Optionally refresh the comments
        } catch (error) {
            console.error('Failed to like comment:', error);
        }
    }, [token, fetchComments]);

    const handleDislikeCommentClick = useCallback(async (commentId) => {
        try {
            const res = await axios.post(
                `${API_ENDPOINTS.COMMENT_REACTION_ENDPOINT}/${commentId}`, { type: 'dislike' },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setUserReactions(prev => ({
                ...prev,
                [commentId]: res.data.current_user_reaction,
            }));

            fetchComments(); // Optionally refresh
        } catch (error) {
            console.error('Failed to dislike comment:', error);
        }
    }, [token, fetchComments]);

    const renderedComments = useMemo(() => (
        comments.map((comment) => (
            <React.Fragment key={comment.id}>
                <CommentItem
                    comment={comment}
                    onReplyClick={handleReplyClick}
                    onLikeCommentClick={handleLikeCommentClick}
                    onDislikeCommentClick={handleDislikeCommentClick}
                    userReaction={userReactions[comment.id]}
                />
                {comment.childrens && 
                    <ChildComments
                        childrens={comment.childrens}
                        onReplyClick={handleReplyClick}
                        onLikeCommentClick={handleLikeCommentClick}
                        onDislikeCommentClick={handleDislikeCommentClick}
                        userReaction={userReactions[comment.id]}
                    />}
            </React.Fragment>
        ))
    ), [comments, handleReplyClick, handleLikeCommentClick, handleDislikeCommentClick, userReactions]);

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return count > 0 ? (
        <div className="comments_wrapper mb-5">
            <h3 className="mb-5 fw-600 text-7">{count === 1 ? '1 Comment' : `${count} Comments`}</h3>
            <ul className="comment_wrapper p-0 m-0">{renderedComments}</ul>
        </div>
    ) : null;
};

const CommentItem = React.memo(({ comment, onReplyClick, onLikeCommentClick, onDislikeCommentClick, userReaction }) => (
    <li className="comment-thread d-flex position-relative mb-4">
        <div className="comment-in d-flex position-relative align-items-start w-100">
            <div className="comment-avatar-wrp">
                <img src={authorImg} alt={comment.comment_author} width={70} className="img-fluid rounded-circle" />
            </div>
            <div className="comment_text d-flex gap10 flex-column">
                <div className="name-wrp d-flex fw-500 align-items-center gap5">
                    <div className="lh-1">{comment.comment_author}</div>
                    <span className="fw-400">{comment.comment_date}</span>
                </div>
                <SanitizedHTML className="text-2 comment-content text-muted" html={comment.comment_content} tag="p" />
                <div className="meta-wrp d-flex align-items-center gap10">
                    <div className="reaction-btn like-btn d-flex align-items-center">
                        <button
                        className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'like' ? 'active' : ''}`}
                        onClick={() => onLikeCommentClick(comment.id)}
                        aria-label={`Like this comment`}
                        >
                            <FontAwesomeIcon icon={faThumbsUp} className="text-muted" />
                            {comment.likes || 0}
                        </button>
                    </div>
                    <div className="reaction-btn dislike-btn d-flex align-items-center">
                        <button
                        className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'dislike' ? 'active' : ''}`}
                        onClick={() => onDislikeCommentClick(comment.id)}
                        aria-label={`Reply to ${comment.comment_author}`}
                        >
                            <FontAwesomeIcon icon={faThumbsDown} className="text-muted" />
                            {comment.dislikes || 0}
                        </button>
                    </div>
                    <div className="reply d-flex align-items-center">
                        <button
                        className="post-reply fw-400 text-decoration-none text-2 text-dark smooth-scroll d-flex align-items-center gap5 ls-1"
                        onClick={() => onReplyClick(comment.id)}
                        aria-label={`Reply to ${comment.comment_author}`}
                        >
                            <FontAwesomeIcon icon={faCommentAlt} className="text-muted" />
                            Reply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </li>
));

const ChildComments = React.memo(({ childrens, onReplyClick, onLikeCommentClick, onDislikeCommentClick, userReaction }) => (
    <>
        {Object.values(childrens).map((child) => (
            <li key={child.id} className="comment-reply mt-4 ms-4 d-flex position-relative flex-column">
                <div className="comment-in d-flex position-relative align-items-start w-100">
                    <div className="comment-avatar-wrp">
                        <img src={authorImg} alt={child.comment_author} width={70} className="img-fluid rounded-circle" />
                    </div>
                    <div className="comment_text d-flex gap10 flex-column">
                        <div className="name-wrp d-flex fw-500 align-items-center gap5">
                            <div className="lh-1">{child.comment_author}</div>
                            <span className="fw-400">{child.comment_date}</span>
                        </div>    
                        <SanitizedHTML className="text-2 text-muted comment-content" html={child.comment_content} tag="p" />
                        <div className="meta-wrp d-flex align-items-center gap10">
                            <div className="reaction-btn like-btn d-flex align-items-center">
                                <button
                                className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'like' ? 'active' : ''}`}
                                onClick={() => onLikeCommentClick(child.id)}
                                aria-label={`Like this comment`}
                                >
                                    <FontAwesomeIcon icon={faThumbsUp} className="text-muted" />
                                    {child.likes || 0}
                                </button>
                            </div>
                            <div className="reaction-btn dislike-btn d-flex align-items-center">
                                <button
                                className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'dislike' ? 'active' : ''}`}
                                onClick={() => onDislikeCommentClick(child.id)}
                                aria-label={`Reply to ${child.comment_author}`}
                                >
                                    <FontAwesomeIcon icon={faThumbsDown} className="text-muted" />
                                    {child.dislikes || 0}
                                </button>
                            </div>
                            <div className="reply d-flex align-items-center">
                                <button
                                    className="post-reply fw-400 text-decoration-none text-2 text-dark smooth-scroll d-flex align-items-center gap5 ls-1"
                                    onClick={() => onReplyClick(child.id)}
                                    aria-label={`Reply to ${child.comment_author}`}
                                >
                                    <FontAwesomeIcon icon={faCommentAlt} className="" />
                                    Reply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {child.childrens && (
                    <ul className="w-100 mb-4">
                        {Object.values(child.childrens).map((grandChild) => (
                            <li key={grandChild.id} className="mt-4 ms-4 comment-reply child d-flex position-relative">
                                <div className="comment-in d-flex position-relative align-items-start w-100">
                                    <div className="comment-avatar-wrp">
                                        <img src={authorImg} alt={grandChild.comment_author} width={70} className="img-fluid rounded-circle me-2" />
                                    </div>
                                    <div className="comment_text d-flex gap10 flex-column">
                                        <div className="name-wrp d-flex fw-500 align-items-center gap5">
                                            <div className="lh-1">{grandChild.comment_author}</div>
                                            <span className="fw-400">{grandChild.comment_date}</span>
                                        </div>
                                        <SanitizedHTML className="text-2 text-muted comment-content" html={grandChild.comment_content} tag="p" />
                                        <div className="meta-wrp d-flex align-items-center gap10">
                                            <div className="reaction-btn like-btn d-flex align-items-center">
                                                <button
                                                className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'like' ? 'active' : ''}`}
                                                onClick={() => onLikeCommentClick(grandChild.id)}
                                                aria-label={`Like this comment`}
                                                >
                                                    <FontAwesomeIcon icon={faThumbsUp} className="text-muted" />
                                                    {grandChild.likes || 0}
                                                </button>
                                            </div>
                                            <div className="reaction-btn dislike-btn d-flex align-items-center">
                                                <button
                                                className={`post-reply fw-400 text-2 d-flex align-items-center gap5 ls-1 ${userReaction === 'dislike' ? 'active' : ''}`}
                                                onClick={() => onDislikeCommentClick(grandChild.id)}
                                                aria-label={`Reply to ${grandChild.comment_author}`}
                                                >
                                                    <FontAwesomeIcon icon={faThumbsDown} className="text-muted" />
                                                    {grandChild.dislikes || 0}
                                                </button>
                                            </div>
                                            <div className="reply d-flex align-items-center">
                                                <button
                                                    className="post-reply fw-400 text-decoration-none text-2 text-dark smooth-scroll d-flex align-items-center gap5 ls-1"
                                                    onClick={() => onReplyClick(grandChild.id)}
                                                    aria-label={`Reply to ${grandChild.comment_author}`}
                                                >
                                                    <FontAwesomeIcon icon={faCommentAlt} className="me-2 text-4" />
                                                    Reply
                                                </button>
                                            </div>
                                        </div>    
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        ))}
    </>
));

Comments.propTypes = {
    id: PropTypes.number.isRequired,
    updateVariable: PropTypes.func.isRequired,
    targetRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

CommentItem.propTypes = {
    comment: PropTypes.object.isRequired,
    onReplyClick: PropTypes.func.isRequired,
    onLikeCommentClick: PropTypes.func.isRequired,
    onDislikeCommentClick: PropTypes.func.isRequired,
    userReaction: PropTypes.string,
};

ChildComments.propTypes = {
    childrens: PropTypes.array.isRequired,
    onReplyClick: PropTypes.func.isRequired,
    onLikeCommentClick: PropTypes.func.isRequired,
    onDislikeCommentClick: PropTypes.func.isRequired,
    userReaction: PropTypes.string,
};

export default Comments;
